import { createTheme } from '@fluentui/react';
import { createPalette } from '../../../../web/utils/getHelputils';
import { Environment, SourceApps } from '../../../models/constants/constants';
import { IAppState } from '../../../models/reduxState';
import {
  getM365Theme,
} from '../controls/customizations/styles/m365Theme';
import { M365ThemeKind } from '../controls/customizations/styles/theme.types';
import { IM365ExtendedSemanticColors } from '../controls/SubwayNav/M365Theme.types';
import { IContactUsTheme } from './theme.model';

export const color = {
  blue: '#337ab7',
  blue2: '#0067b8',
  blue3: '#2e6da4',
  blue4: '#0079d4',
  white: '#FFF',
  black: '#000',
  gray: '#f0f0f0',
  gray1: '#1B1A19',
  gray2: '#a1a1a1',
  gray3: '#eee',
  gray4: '#555',
  grayA: '#F3F3F3',
  grayB: '#EAEAEA',
  grayG: '#444444',
  grayH: '#363636',
  grayI: '#252525',
  lightGray: '#aaa',
  lightGray2: '#D3D3D3',
  iconDarkGray: '#484644',
  fluentGray: '#828282',
  fluentGray2: '#e5e5e5',
  darkGray: '#2e2e2e',
  lightBlueLinkA: '#006cb4',
  lightBlueLinkB: '#7acbfc',
  transparentWhite: 'rgba(255,255,255,.4)',
  darkBlack: '#0a0a0a',
  red: '#FF0000',
  darkBlue: '#006CBE',
  darkBlue2: '#286090',
  darkBlue3: '#204d74',
  darkBlue4: '#122b40',
  darkBlue5: '#004377',
  purple: '#5b5fc7',
  darkPurple: '#464775',
  lightPurple: '#7f85f5',
  highContrastYellow: '#ffff00',
};

// we need to load M365Themes for an extension of those themes for admin-controls
const defaultFabricTheme = getM365Theme(M365ThemeKind.FabricDefaultTheme);

const teamsThemes: { [key: string]: IContactUsTheme } = {
  black: {
    ...defaultFabricTheme,
    extensionPalettes: {
      panelBackground: '#292929',
      neutralTertiary: '#616161',
      neutralPrimary: color.purple,
      themePrimary: color.darkPurple,
      lightBlueLink: color.lightPurple,
      white: color.white,
      header: color.white,
      black: '#fff',
      gray: color.gray2,
      red: color.red,
      cardBackground: color.grayH,
      cardHeader: color.grayH,
      inputBackground: color.grayG,
      inputColor: '#fff',
      primaryBtn: color.purple,
      primaryBtnHover: color.darkPurple,
      primaryBtnFocus: color.darkPurple,
      primaryBtnFocusBorder: color.darkPurple,
      disabledInputBackground: color.grayG,
      footerBorder: color.grayH,
      caption: color.white,
      icon: color.lightPurple,
      clearButtonIcon: color.lightPurple,
      searchBoxBorder: 'none',
      searchBoxBackground: color.grayG,
      footerBackground: '#1f1f1f',
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.darkPurple,
      premierBadgeBackground: color.highContrastYellow,
      notificationBackground: color.grayI,
    },
  },
  highcontrastblack: {
    ...defaultFabricTheme,
    extensionPalettes: {
      panelBackground: '#000',
      neutralTertiary: '#616161',
      neutralPrimary: '#1aebff',
      themePrimary: '#1aebff',
      lightBlueLink: color.highContrastYellow,
      white: '#242424',
      header: color.white,
      black: '#fff',
      gray: color.gray2,
      red: color.red,
      cardBackground: color.grayH,
      cardHeader: color.grayH,
      inputBackground: '#000',
      inputColor: color.white,
      primaryBtn: '#1aebff',
      primaryBtnHover: color.highContrastYellow,
      primaryBtnFocus: color.highContrastYellow,
      primaryBtnFocusBorder: color.highContrastYellow,
      disabledInputBackground: color.grayG,
      footerBorder: '#cecece',
      caption: color.white,
      icon: color.white,
      clearButtonIcon: color.white,
      searchBoxBorder: `1px solid #fff`,
      searchBoxBackground: 'transparent',
      footerBackground: '#000',
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.highContrastYellow,
      premierBadgeBackground: color.highContrastYellow,
      notificationBackground: color.grayI,
    },
  },
  colorful: {
    ...defaultFabricTheme,
    extensionPalettes: {
      panelBackground: '#ffffff',
      neutralTertiary: '#616161',
      neutralPrimary: '#5b5fc7',
      themePrimary: color.purple,
      lightBlueLink: color.purple,
      header: color.gray1,
      white: '#fff',
      black: '#242424',
      gray: color.gray2,
      red: color.red,
      cardBackground: color.white,
      cardHeader: color.grayA,
      inputBackground: color.white,
      inputColor: '#242424',
      primaryBtn: color.purple,
      primaryBtnHover: color.darkPurple,
      primaryBtnFocus: color.darkPurple,
      primaryBtnFocusBorder: color.darkPurple,
      disabledInputBackground: color.gray3,
      footerBorder: '#cecece',
      caption: '#767676',
      icon: color.purple,
      clearButtonIcon: '#605E5C',
      searchBoxBorder: 'none',
      searchBoxBackground: color.white,
      footerBackground: '#f5f5f5',
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.darkPurple,
      premierBadgeBackground: color.highContrastYellow,
      notificationBackground: color.grayB,
    },
  },
};

const surfaceAppThemes: { [key: string]: IContactUsTheme } = {
  darkgray: {
    ...defaultFabricTheme,
    extensionPalettes: {
      panelBackground: 'transparent',
      neutralTertiary: '#616161',
      neutralPrimary: '#454545',
      themePrimary: color.blue2,
      lightBlueLink: color.lightBlueLinkB,
      white: color.white,
      header: color.white,
      black: color.white,
      gray: color.gray2,
      red: color.red,
      cardBackground: color.grayG,
      cardHeader: color.grayG,
      primaryBtn: '#373737',
      inputBackground: color.grayG,
      inputColor: color.white,
      primaryBtnHover: '#3c3c3c',
      primaryBtnFocus: '#3c3c3c',
      primaryBtnFocusBorder: '#3c3c3c',
      disabledInputBackground: color.grayG,
      footerBorder: color.white,
      caption: color.white,
      icon: '#909090',
      clearButtonIcon: '#909090',
      searchBoxBorder: '1px solid #484644',
      searchBoxBackground: 'transparent',
      footerBackground: 'transparent',
      premierBadgeBackground: color.highContrastYellow,
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.darkBlue5,
      notificationBackground: color.grayI,
    },
  },
  white: {
    ...defaultFabricTheme,
    extensionPalettes: {
      panelBackground: 'transparent',
      neutralTertiary: '#616161',
      neutralPrimary: '#d2d2d2',
      themePrimary: color.blue2,
      lightBlueLink: color.lightBlueLinkA,
      header: color.gray1,
      white: '#1a1a1a',
      black: '#1a1a1a',
      gray: color.gray2,
      red: color.red,
      cardBackground: '#fbfbfb',
      cardHeader: '#fbfbfb',
      primaryBtn: '#fefefe',
      inputBackground: color.grayA,
      inputColor: '#1a1a1a',
      primaryBtnHover: '#fafafa',
      primaryBtnFocus: '#fafafa',
      primaryBtnFocusBorder: color.white,
      disabledInputBackground: color.gray3,
      footerBorder: '#cecece',
      caption: '#767676',
      icon: color.blue4,
      clearButtonIcon: '#605E5C',
      searchBoxBorder: '1px solid #484644',
      searchBoxBackground: 'transparent',
      footerBackground: 'transparent',
      premierBadgeBackground: color.highContrastYellow,
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.darkBlue5,
      notificationBackground: color.grayB,
    },
  },
};

const outlookDotComThemes: { [key: string]: IContactUsTheme } = {
  fluent: {
    ...defaultFabricTheme,
    extensionPalettes: {
      panelBackground: color.white,
      neutralPrimary: color.blue3,
      themePrimary: color.blue2,
      lightBlueLink: color.lightBlueLinkA,
      neutralTertiary: color.gray2,
      header: color.gray1,
      white: color.white,
      black: color.black,
      gray: color.gray2,
      red: color.red,
      cardBackground: color.grayA,
      cardHeader: color.grayA,
      inputBackground: color.grayA,
      inputColor: color.black,
      primaryBtn: color.blue,
      primaryBtnHover: color.darkBlue2,
      primaryBtnFocus: color.darkBlue2,
      primaryBtnFocusBorder: color.darkBlue2,
      disabledInputBackground: color.gray3,
      footerBorder: '#cecece',
      caption: '#767676',
      icon: color.blue4,
      clearButtonIcon: '#605E5C',
      searchBoxBorder: '1px solid #484644',
      searchBoxBackground: 'transparent',
      footerBackground: color.white,
      premierBadgeBackground: color.highContrastYellow,
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.darkBlue5,
      notificationBackground: color.grayB,
    },
  },
  black: {
    ...defaultFabricTheme,
    extensionPalettes: {
      panelBackground: color.darkBlack,
      neutralPrimary: color.fluentGray,
      neutralTertiary: color.iconDarkGray,
      themePrimary: color.lightBlueLinkA,
      lightBlueLink: color.lightBlueLinkB,
      header: color.white,
      white: color.white,
      black: color.white,
      gray: color.gray2,
      red: color.red,
      cardBackground: color.grayH,
      cardHeader: color.grayH,
      inputBackground: color.grayH,
      inputColor: color.white,
      primaryBtn: color.transparentWhite,
      primaryBtnHover: color.darkGray,
      primaryBtnFocus: color.darkGray,
      primaryBtnFocusBorder: color.darkGray,
      disabledInputBackground: color.grayG,
      footerBorder: '#cecece',
      caption: '#767676',
      icon: color.blue4,
      clearButtonIcon: '#605E5C',
      searchBoxBorder: '1px solid #484644',
      searchBoxBackground: 'transparent',
      footerBackground: color.darkBlack,
      premierBadgeBackground: color.highContrastYellow,
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.darkBlue5,
      notificationBackground: color.grayI,
    },
  },
};

export const toggleDefaultTheme: IContactUsTheme = {
  ...defaultFabricTheme,
  extensionPalettes: {
    panelBackground: color.white,
    neutralPrimary: color.blue3,
    themePrimary: color.blue2,
    lightBlueLink: color.lightBlueLinkA,
    neutralTertiary: color.gray2,
    header: color.gray1,
    white: color.white,
    black: color.black,
    gray: color.gray2,
    red: color.red,
    cardBackground: color.grayA,
    cardHeader: color.grayA,
    inputBackground: color.white,
    inputColor: color.black,
    primaryBtn: color.blue,
    primaryBtnHover: color.darkBlue2,
    primaryBtnFocus: color.darkBlue3,
    primaryBtnFocusBorder: color.darkBlue4,
    disabledInputBackground: color.gray3,
    footerBorder: '#cecece',
    caption: '#767676',
    icon: color.blue4,
    clearButtonIcon: '#605E5C',
    searchBoxBorder: '1px solid #484644',
    searchBoxBackground: 'transparent',
    footerBackground: color.white,
    premierBadgeBackground: color.highContrastYellow,
    upsellContainerBorder: color.lightGray2,
    upsellSubdescription: '#605E5C',
    upsellDefaultHover: '#C8C8C8',
    uploadButtonHover: color.darkBlue5,
    notificationBackground: color.grayB,
  },
};

export const toggleColorful: IContactUsTheme = {
  ...defaultFabricTheme,
  extensionPalettes: {
    panelBackground: color.gray,
    neutralPrimary: color.blue,
    themePrimary: color.lightBlueLinkA,
    lightBlueLink: color.lightBlueLinkA,
    neutralTertiary: color.gray2,
    header: color.gray1,
    white: color.white,
    black: color.black,
    gray: color.gray2,
    red: color.red,
    cardBackground: color.white,
    cardHeader: color.fluentGray2,
    inputBackground: color.white,
    inputColor: color.black,
    primaryBtn: color.blue,
    primaryBtnHover: color.darkBlue2,
    primaryBtnFocus: color.darkBlue2,
    primaryBtnFocusBorder: color.darkBlue2,
    disabledInputBackground: color.gray3,
    footerBorder: '#cecece',
    caption: '#767676',
    icon: color.blue4,
    clearButtonIcon: '#605E5C',
    searchBoxBorder: '1px solid #484644',
    searchBoxBackground: color.white,
    footerBackground: color.gray,
    premierBadgeBackground: color.highContrastYellow,
    upsellContainerBorder: color.lightGray2,
    upsellSubdescription: '#605E5C',
    upsellDefaultHover: '#C8C8C8',
    uploadButtonHover: color.darkBlue5,
    notificationBackground: '#DADADA',
  },
};

export const toggleDarkgray: IContactUsTheme = {
  ...defaultFabricTheme,
  extensionPalettes: {
    panelBackground: color.darkGray,
    neutralPrimary: color.fluentGray,
    neutralTertiary: color.iconDarkGray,
    themePrimary: color.lightBlueLinkA,
    lightBlueLink: color.lightBlueLinkB,
    header: color.white,
    white: color.white,
    black: color.white,
    gray: color.gray2,
    red: color.red,
    cardBackground: color.grayG,
    cardHeader: color.grayG,
    inputBackground: color.grayG,
    inputColor: color.white,
    primaryBtn: color.transparentWhite,
    primaryBtnHover: color.darkGray,
    primaryBtnFocus: color.darkGray,
    primaryBtnFocusBorder: color.darkGray,
    disabledInputBackground: color.grayG,
    footerBorder: '#cecece',
    caption: color.white,
    icon: '#d1e6ff',
    clearButtonIcon: '#d1e6ff',
    searchBoxBorder: '1px solid #484644',
    searchBoxBackground: color.grayG,
    footerBackground: color.darkGray,
    premierBadgeBackground: color.highContrastYellow,
    upsellContainerBorder: color.lightGray2,
    upsellSubdescription: '#605E5C',
    upsellDefaultHover: '#C8C8C8',
    uploadButtonHover: color.darkBlue5,
    notificationBackground: color.grayG,
  },
};

export const toggleBlack: IContactUsTheme = {
  ...defaultFabricTheme,
  extensionPalettes: {
    panelBackground: color.darkBlack,
    neutralPrimary: color.fluentGray,
    neutralTertiary: color.iconDarkGray,
    themePrimary: color.lightBlueLinkA,
    lightBlueLink: color.lightBlueLinkB,
    header: color.white,
    white: color.white,
    black: color.white,
    gray: color.gray2,
    red: color.red,
    cardBackground: color.grayH,
    cardHeader: color.grayH,
    inputBackground: color.grayH,
    inputColor: color.white,
    primaryBtn: color.transparentWhite,
    primaryBtnHover: color.darkGray,
    primaryBtnFocus: color.darkGray,
    primaryBtnFocusBorder: color.darkGray,
    disabledInputBackground: color.grayG,
    footerBorder: '#cecece',
    caption: color.white,
    icon: '#d1e6ff',
    clearButtonIcon: '#d1e6ff',
    searchBoxBorder: '1px solid #484644',
    searchBoxBackground: color.grayG,
    footerBackground: color.darkBlack,
    premierBadgeBackground: color.highContrastYellow,
    upsellContainerBorder: color.lightGray2,
    upsellSubdescription: '#605E5C',
    upsellDefaultHover: '#C8C8C8',
    uploadButtonHover: color.darkBlue5,
    notificationBackground: color.gray1,
  },
};

export const toggleWhite: IContactUsTheme = {
  ...defaultFabricTheme,
  extensionPalettes: {
    panelBackground: color.gray,
    neutralPrimary: color.blue,
    neutralTertiary: color.gray2,
    themePrimary: color.lightBlueLinkA,
    lightBlueLink: color.lightBlueLinkA,
    header: color.gray1,
    white: color.white,
    black: color.black,
    gray: color.gray2,
    red: color.red,
    cardBackground: color.white,
    cardHeader: color.white,
    inputBackground: color.white,
    inputColor: color.black,
    primaryBtn: color.blue,
    primaryBtnHover: color.darkBlue2,
    primaryBtnFocus: color.darkBlue2,
    primaryBtnFocusBorder: color.darkBlue2,
    disabledInputBackground: color.gray3,
    footerBorder: '#cecece',
    caption: '#767676',
    icon: color.blue4,
    clearButtonIcon: '#605E5C',
    searchBoxBorder: '1px solid #484644',
    searchBoxBackground: color.white,
    footerBackground: color.gray,
    premierBadgeBackground: color.highContrastYellow,
    upsellContainerBorder: color.lightGray2,
    upsellSubdescription: '#605E5C',
    upsellDefaultHover: '#C8C8C8',
    uploadButtonHover: color.darkBlue5,
    notificationBackground: color.grayB,
  },
};

export const getToggleInAppTheme = (theme: string): IContactUsTheme => {
  let currentTheme = toggleDefaultTheme;
  switch (theme.toLowerCase()) {
    case 'fluentdarkgray':
    case 'darkgray':
      currentTheme = toggleDarkgray;
      break;
    case 'fluentblack':
    case 'black':
      currentTheme = toggleBlack;
      break;
    case 'fluentwhite':
    case 'white':
      currentTheme = toggleWhite;
      break;
    case 'fluentcolorful':
    case 'colorful':
      currentTheme = toggleColorful;
      break;
    default:
      break;
  }
  currentTheme.extensionPalettes.panelBackground = 'transparent';
  return currentTheme;
};

export const getTeamsTheme = (theme: string): IContactUsTheme => {
  let teamsTheme = teamsThemes['colorful'];
  if (theme) {
    teamsTheme = teamsThemes[theme.toLowerCase()] || teamsThemes['colorful'];
  }
  return teamsTheme;
};

export const getOutlookDotComTheme = (theme: string): IContactUsTheme => {
  let outlookDotComTheme = outlookDotComThemes['fluent'];
  if (!!theme) {
    outlookDotComTheme = outlookDotComThemes[theme.toLowerCase()] || outlookDotComThemes['fluent'];
  }
  return outlookDotComTheme;
};

export const getSurfaceAppTheme = (theme: string): IContactUsTheme => {
  let surfaceAppTheme = surfaceAppThemes['white'];
  if (!!theme) {
    surfaceAppTheme = surfaceAppThemes[theme.toLowerCase()] || surfaceAppThemes['white'];
  }
  return surfaceAppTheme;
};

export const getOneDriveWebTheme = (theme: string): IContactUsTheme => {
  let oneDriveWebTheme = toggleDefaultTheme;
  if (!!theme && theme === 'darkgray') {
    oneDriveWebTheme = toggleDarkgray;
  }
  return oneDriveWebTheme;
};

export const getGetHelpTheme = (theme: string, accentColor: string): IContactUsTheme => {
  const getHelpTheme = createTheme({
    palette: createPalette(accentColor, theme),
    isInverted: theme === 'Dark',
  });
  const defaultGetHelpTheme: IContactUsTheme = {
    // Todo: Brook extensionPalettes should be removed
    extensionPalettes: {
      panelBackground: color.gray,
      neutralPrimary: color.blue,
      neutralTertiary: color.gray2,
      themePrimary: color.lightBlueLinkA,
      lightBlueLink: color.lightBlueLinkA,
      header: color.gray1,
      white: color.white,
      black: color.black,
      gray: color.gray2,
      red: color.red,
      cardBackground: color.white,
      cardHeader: color.white,
      inputBackground: color.white,
      inputColor: color.black,
      primaryBtn: color.blue,
      primaryBtnHover: color.darkBlue2,
      primaryBtnFocus: color.darkBlue2,
      primaryBtnFocusBorder: color.darkBlue2,
      disabledInputBackground: color.gray3,
      footerBorder: '#cecece',
      caption: '#767676',
      icon: color.blue4,
      clearButtonIcon: '#605E5C',
      searchBoxBorder: '1px solid #484644',
      searchBoxBackground: color.white,
      footerBackground: color.gray,
      premierBadgeBackground: color.highContrastYellow,
      upsellContainerBorder: color.lightGray2,
      upsellSubdescription: '#605E5C',
      upsellDefaultHover: '#C8C8C8',
      uploadButtonHover: color.darkBlue5,
      notificationBackground: color.grayB,
    },
    m365: defaultFabricTheme.m365,
    ...getHelpTheme,
    semanticColors: { ...defaultFabricTheme.semanticColors, ...getHelpTheme.semanticColors } as IM365ExtendedSemanticColors,
  };
  return defaultGetHelpTheme;
};

export const getTheme = (state: IAppState): IContactUsTheme => {
  const sourceApp = state.GlobalState.sourceApp;
  const environment: Environment = state.GlobalState.environment;
  const themeName = state.GlobalState.theme;
  const accentColor = state.GlobalState.accentColor;
  let contactUsTheme = toggleDefaultTheme;

  if (sourceApp) {
    switch (sourceApp.toLowerCase()) {
      case SourceApps.Teams:
      case SourceApps.Teams2:
      case SourceApps.TeamsAdmin:
      case SourceApps.TeamsAdmin2:
        contactUsTheme = getTeamsTheme(themeName);
        break;
      case SourceApps.SurfaceApp:
      case SourceApps.SurfaceApp2:
        contactUsTheme = getSurfaceAppTheme(themeName);
        break;
      case SourceApps.OneDriveWeb:
        contactUsTheme = getOneDriveWebTheme(themeName);
        break;
      default:
        break;
    }
  }
  switch (environment) {
    case Environment.nonAgavePane:
      contactUsTheme = getToggleInAppTheme(themeName);
      break;
    case Environment.OutlookDotCom:
      contactUsTheme = getOutlookDotComTheme(themeName);
      break;
    case Environment.GetHelp:
      contactUsTheme = getGetHelpTheme(themeName, accentColor);
      break;
    default:
      break;
  }
  return contactUsTheme;
};

export interface IThemedProps {
  // Todo: when theming work is done we should remove the null type
  theme: IContactUsTheme | null;
}
